import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, EventEmitter, HostBinding, inject, Input, Output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { MOBILE_LAYOUT_QUERIES } from '../../constants/responsive-breakpoints';

export type WidgetIconColor = 'red' | 'yellow' | 'orange' | 'blue' | 'green' | 'cyan';

export interface WidgetBlock {
  name: string;
  value: number | string | null;
  description?: string;
  descriptionIcon?: string;
  descriptionColor?: WidgetIconColor;
  icon?: string;
  iconColor?: WidgetIconColor;
  tooltip?: string;
}

export interface WidgetBlockClickEvent {
  blockIndex: number;
  block: WidgetBlock;
}

@Component({
  selector: 'app-ui-widget-card',
  templateUrl: './ui-widget-card.component.html',
  styleUrls: ['./ui-widget-card.component.scss'],
  standalone: false,
})
export class UiWidgetCardComponent {
  private readonly isMobileViewport = toSignal(
    inject(BreakpointObserver)
      .observe(MOBILE_LAYOUT_QUERIES)
      .pipe(map((result) => result.matches)),
    { initialValue: MOBILE_LAYOUT_QUERIES.some((query) => window.matchMedia(query).matches) }
  );

  @Input() name: string;
  @Input() value: number | string | null;
  @Input() description: string;
  @Input() descriptionIcon: string;
  @Input() descriptionColor: WidgetIconColor;
  @Input() icon: string;
  @Input() iconColor: WidgetIconColor;
  @Input() statusName = '';
  @Input() statusIcon = '';
  @Input() statusColor?: WidgetIconColor;
  @Input() blockLabels: string[] = [];
  @Input() statusBlockOrder: number[] = [];
  @Input() statusOnMobile = false;

  /**
   * Array of blocks for dual-mode widget.
   */
  @Input() blocks: WidgetBlock[];

  /** Whether the card is clickable (shows hover effect and cursor pointer). */
  @Input() clickable = false;

  /** Whether to show a loading spinner in the bottom right corner. */
  @Input() loading = false;

  /** Emits when a specific block is clicked (for dual-mode widgets). */
  @Output() blockClick = new EventEmitter<WidgetBlockClickEvent>();

  @HostBinding('class.widget-single') get singleClass(): boolean {
    return this.isSingleMode;
  }

  @HostBinding('class.widget-dual') get dualClass(): boolean {
    return !this.isSingleMode;
  }

  get isSingleMode(): boolean {
    return !!this.name && (!this.blocks || this.blocks.length === 0);
  }

  get isStatusMode(): boolean {
    return !!this.statusName;
  }

  get showStatusMode(): boolean {
    return this.isStatusMode && (!this.statusOnMobile || this.isMobileViewport());
  }

  get statusDisplayBlocks(): WidgetBlock[] {
    if (!this.statusBlockOrder.length) {
      return this.displayBlocks;
    }

    return this.statusBlockOrder.map((index) => this.displayBlocks[index]).filter(Boolean);
  }

  get displayBlocks(): WidgetBlock[] {
    if (this.isSingleMode) {
      return [
        {
          name: this.name,
          value: this.value,
          description: this.description,
          descriptionIcon: this.descriptionIcon,
          descriptionColor: this.descriptionColor,
          icon: this.icon,
          iconColor: this.iconColor,
        },
      ];
    }
    return this.blocks || [];
  }

  onBlockClick(blockIndex: number, block: WidgetBlock, event: Event): void {
    if (this.clickable && !this.isSingleMode) {
      event.stopPropagation();
      this.blockClick.emit({ blockIndex, block });
    }
  }

  onStatusBlockClick(blockIndex: number, block: WidgetBlock, event: Event): void {
    const originalIndex = this.statusBlockOrder[blockIndex] ?? blockIndex;
    this.onBlockClick(originalIndex, block, event);
  }
}
